'use client';

import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/nextjs';

import { ProductItemPropTypes } from '@/components/Card/Product';
import { useUser } from '@/context/userContext';
import { endpoints, http, logger } from '@/config';
import { GENERIC_ERROR_MESSAGE } from '@/constants';
import { useToggle } from '@/hooks';
import { storage } from '@/utils/storage';
import { useCleverTap } from './clevertapContext';
import { getCartRecommendedProductsWizzy } from '@/utils/wizzyHelper';
import {
  getFormattedPaymentDetails,
  removeFromStorage,
  searchParams,
  pushGA4Event,
} from '@/utils/helper';
import { checkCookiesAction, generateCartIdAction } from '@/app/actions';
import { deleteCookie, getCookie } from 'cookies-next';

export const INTERVAL = 5000;

type AttributesOptionTypes = {
  option_id?: string;
  option_value?: string;
};

type ParsedLoginResponse = {
  userDetails?: { customer_id: string; store_id: string };
  auth_token?: string;
};

type CartTypes = {
  name?: string;
  sku: string | undefined;
  qty?: number;
  item_id?: number;
  quote_id?: string;
  type_id?: string;
  gift?: AttributesOptionTypes;
  giftMessage?: AttributesOptionTypes;
  size?: AttributesOptionTypes;
  options?: any;
  product_option?: any;
  checked?: boolean;
  greeting_card?: AttributesOptionTypes;
  recipient_name?: AttributesOptionTypes;
  selectedGiftBoxItems?: {
    sku: string;
    qty: number;
  }[];
  // support bundle options for bento box
  bundle_options?: Array<{
    option_id: number;
    option_qty: number;
    option_selections: number[];
  }>;
};

interface CartPropTypes {
  item: CartTypes;
  type?: keyof typeof USERS_TYPES;
  method?: 'post' | 'put';
  id?: {
    [key: string]: string;
  };
  multiple?: boolean;
  options?: {
    callback?: () => void;
    showToast?: boolean;
    updateCart?: boolean;
  };
}

export const USERS_TYPES = {
  guest: {
    key: 'guest',
    id: 'cart_id',
  },
  customer: {
    key: 'customer',
    id: 'quote_id',
  },
};

interface PropTypes {
  children: ReactNode;
}

interface useCartPropTypes {
  addToCart: (
    product: CartTypes,
    method?: 'put' | 'post',
    multiple?: boolean,
    options?: {
      callback?: () => void;
      showToast?: boolean;
    }
  ) => Promise<{ item: CartTypes; success: boolean; message: string }>;
  addToCartProduct: (
    product: CartTypes,
    method?: 'put' | 'post',
    multiple?: boolean,
    options?: {
      callback?: () => void;
      showToast?: boolean;
      updateCart?: boolean;
    }
  ) => Promise<CartOperationResult>;
  updateCart: ({ item, id }: CartPropTypes) => void;
  fetchUpdateCart: () => void;
  postLoginCartOperations: (parsedRes: ParsedLoginResponse) => void;
  cartItems: ProductItemPropTypes[] | null;
  paymentDetails: any;
  removeFromCart: (item: any) => Promise<void>;
  isCartSidebarOpen: boolean;
  movDisable: boolean;
  toggleCartSidebar: (value: boolean) => void;
  recommendations: Record<
    string,
    {
      name: string;
      products: any[];
    }
  > | null;
  loadingRecommended: boolean;
  cartToast?: {
    message: string;
    type: 'success' | 'failure';
  };
  movMessage?: string;
  movAmount?: number;
  cartValue: number;
  cartIsLoading: boolean;
  cartItemsMap: Map<string, ProductItemPropTypes>;
  clearCart: () => Promise<void>;
  isProductPage: boolean;
  setIsProductPage: (value: boolean) => void;
  cartItemsCount?: number;
}

const Context = createContext<any | null>(null);

// Add type for cart operation result
type CartOperationResult = {
  item: CartTypes;
  success: boolean;
};

const CartProvider: FC<PropTypes> = ({ children }) => {
  const { userDetails, updateUserDetails } = useUser();
  const { updateUserCartClevertap } = useCleverTap();
  const cartOpen =
    searchParams.get('cartOpen') && searchParams.get('cartOpen') === 'true';
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [movMessage, setMovMessage] = useState<string>();
  const [movAmount, setMovAmount] = useState<number>();
  const [cartValue, setCartValue] = useState<number>(0);
  const [cartToast, setCartToast] = useState<
    | {
        message: string;
        type: 'success' | 'failure';
      }
    | undefined
  >();
  const { value: isCartSidebarOpen, toggleValue: toggleCartSidebar } =
    useToggle(!!cartOpen);
  const { value: movDisable, toggleValue: toggleMov } = useToggle(false);
  const [cartItems, setCartItems] = useState<ProductItemPropTypes[]>([]);
  const [cartItemsMap, setCartItemsMap] = useState<
    Map<string, ProductItemPropTypes>
  >(new Map());
  const cartItemsCount = useMemo(
    () =>
      cartItems
        ? cartItems.filter(item =>
            !!item?.extension_attributes?.gift_box_item_id &&
            item?.extension_attributes?.is_gift_box !== '1' &&
            item?.extension_attributes?.is_greeting_card !== 1
              ? false
              : true
          ).length
        : undefined,
    [cartItems]
  );
  const { value: loadingRecommended, toggleValue: toggleLoadingRecommended } =
    useToggle(false);
  const [recommendations, setRecommendations] = useState<Record<
    string,
    {
      name: string;
      products: any[];
    }
  > | null>(null);
  const { pushEvent } = useCleverTap();
  const { value: cartIsLoading, toggleValue: setCartIsLoading } =
    useToggle(true);
  const [isProductPage, setIsProductPage] = useState(false);

  // Add queue related state and refs
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const cartQueue = useRef<
    Array<{
      product: CartTypes;
      method: 'put' | 'post';
      multiple?: boolean;
      options?: {
        callback?: () => void;
        showToast?: boolean;
        updateCart?: boolean;
      };
      resolve: (value: CartOperationResult) => void;
    }>
  >([]);

  const getRecommendedProducts = useCallback(async () => {
    toggleLoadingRecommended(true);
    const resultsObj = await getCartRecommendedProductsWizzy({ cartItems });
    setRecommendations(resultsObj);
    toggleLoadingRecommended(false);
  }, [cartItems]);

  const fetchUpdateCart = useCallback(async () => {
    const guestCartId: string = storage.get(USERS_TYPES.guest.id) || '';
    const isCustomer = storage.get('token');

    const ENDPOINT = isCustomer
      ? `${endpoints.CUSTOMER_CART}/items`
      : `${endpoints.GUEST_CART}/${guestCartId}/items`;

    const PAYMENT_ENDPOINT = isCustomer
      ? `${endpoints.CUSTOMER_CART}/payment-information`
      : `${endpoints.GUEST_CART}/${guestCartId}/payment-information`;
    if (!guestCartId && !isCustomer) {
      setPaymentDetails(null);
      setCartItems([]);
      setCartItemsMap(new Map());
      updateUserCartClevertap({ cartItems: [] });
      return;
    }

    try {
      setCartIsLoading(true);
      const [cartResponse, paymentResponse] = await Promise.all([
        http.get(ENDPOINT),
        http.get(PAYMENT_ENDPOINT),
      ]);
      if (cartResponse?.response?.ok) {
        let details = getFormattedPaymentDetails(
          paymentResponse?.data?.totals?.total_segments
        );
        details = {
          ...details,
          couponCode: paymentResponse?.data?.totals?.coupon_code,
        };
        const orderValueSubtotal =
          details?.others.find(
            (item: { code: string; value: number }) => item?.code === 'subtotal'
          )?.value ??
          paymentResponse?.data?.totals?.subtotal ??
          0;

        // Always hydrate cartValue so progress-based UI (Weekend of Plenty, etc.)
        // works for existing carts even when MOV data is unavailable.
        setCartValue(orderValueSubtotal);

        if (movAmount) {
          if (movAmount <= orderValueSubtotal) {
            toggleMov(false);
          } else {
            toggleMov(true);
          }
        }
        setCartItems(cartResponse?.data);
        setCartItemsMap(
          new Map(
            cartResponse?.data?.map((item: ProductItemPropTypes) => [
              item.sku,
              item,
            ])
          )
        );
        updateUserCartClevertap({ cartItems: cartResponse?.data });
        setPaymentDetails(details);
      } else {
        // Error code 50003 = "no active cart for user" or 404 = cart not found
        // Clear stale IDs and regenerate a fresh cart
        const errorCode = cartResponse?.data?.code;
        const isNoActiveCart =
          [50003, 50002].includes(errorCode) ||
          cartResponse?.response?.status === 404 ||
          cartResponse?.data?.message ===
            'Current customer does not have an active cart.';

        if (isNoActiveCart) {
          removeFromStorage(['cart_id', 'quote_id']);
          deleteCookie('quote_id');
          deleteCookie('cart_id');
          await generateCartId();
        }

        setPaymentDetails(null);
        setCartItems([]);
        setCartItemsMap(new Map());
        updateUserCartClevertap({ cartItems: [] });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'cart', action: 'fetchUpdateCart' },
        extra: {
          isLoggedIn: !!isCustomer,
          guestCartId: guestCartId,
          endpoint: ENDPOINT,
        },
      });
      setPaymentDetails(null);
      setCartItems([]);
      setCartItemsMap(new Map());
      updateUserCartClevertap({ cartItems: [] });
    } finally {
      setCartIsLoading(false);
    }
  }, [userDetails, movAmount]);

  const postLoginCartOperations = async (parsedRes: ParsedLoginResponse) => {
    try {
      // Update user details
      await updateUserDetails({
        token: parsedRes?.auth_token,
      });

      // Update cart
      fetchUpdateCart();
    } catch (error) {
      logger.log('Error in user or cart update:', error);
    }
  };

  const onCart = useCallback(
    async ({ item, id, options, type }: CartPropTypes) => {
      const { showToast, updateCart = true } = options || {};
      const ENDPOINT = id?.quote_id
        ? `${endpoints.CUSTOMER_CART}/items`
        : `${endpoints.GUEST_CART}/${id?.cart_id}/items`;

      const request = {
        cartItem: item,
      };

      if (id?.quote_id) request.cartItem.quote_id = id.quote_id;

      try {
        setCartIsLoading(true);
        const sku = item?.sku;
        if (sku) {
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            newMap.set(sku, {
              isLoading: true,
              name: '',
              sku: '',
              parent_item_id: null,
              qty: 0,
            });
            return newMap;
          });
        }
        const { response, data } = await http.post(ENDPOINT, { ...request });
        if (response?.ok) {
          // Set loading states to false before any other operations
          setCartItems(prev => [...prev, data]);
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            newMap.set(data.sku, { ...data, isLoading: false });
            return newMap;
          });
          setCartIsLoading(false);
          if (item?.name && showToast) {
            setCartToast({
              message: `"${item?.name}" added to cart`,
              type: 'success',
            });
            setTimeout(() => {
              setCartToast(undefined);
            }, 2000);
          }

          // Fetch cart update after setting loading states
          if (updateCart) {
            await fetchUpdateCart();
          }
          return {
            item,
            success: true,
            message: `"${item?.name}" added to cart successfully`,
          };
        } else if (
          data?.message === 'No such entity with %fieldName = %fieldValue' ||
          data?.message === '"%fieldName" is required. Enter and try again.' ||
          data?.message === 'Current customer does not have an active cart.'
        ) {
          removeFromStorage(['cart_id', 'quote_id']);
          deleteCookie('quote_id');
          deleteCookie('cart_id');
          Sentry.captureMessage(data?.message, {
            tags: { module: 'cart', action: 'addToCart-invalidQuoteCart' },
            extra: {
              sku: item?.sku,
              qty: item?.qty,
              isLoggedIn: !!userDetails?.id,
              cartId: id?.cart_id,
              quoteId: id?.quote_id,
              errorMessage: JSON.stringify(data),
            },
          });
          return await createQuote({ item, type });
        } else if (
          data?.message === 'Invalid state change requested' ||
          data?.message ===
            "The consumer isn't authorized to access %resources."
        ) {
          removeFromStorage(['cart_id', 'quote_id', 'token']);
          deleteCookie('token');
          deleteCookie('quote_id');
          deleteCookie('cart_id');
          Sentry.captureMessage(data?.message, {
            tags: { module: 'cart', action: 'addToCart-invalidToken' },
            extra: {
              sku: item?.sku,
              qty: item?.qty,
              isLoggedIn: !!userDetails?.id,
              cartId: id?.cart_id,
              quoteId: id?.quote_id,
              errorMessage: JSON.stringify(data),
            },
          });
          return await createQuote({ item, type });
        } else {
          const errorMsg =
            data?.message || JSON.stringify(data) || 'Unknown error occurred';
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        Sentry.captureException(error, {
          tags: { module: 'cart', action: 'addToCart-exception' },
          extra: {
            sku: item?.sku,
            qty: item?.qty,
            isLoggedIn: !!userDetails?.id,
            cartId: id?.cart_id,
            quoteId: id?.quote_id,
            errorMessage: JSON.stringify(error),
          },
        });
        if (showToast) {
          const msg = error?.message;
          if (typeof msg === 'string' && msg !== '[object Object]') {
            toast.error(msg);
          } else if (typeof msg === 'object' && msg !== null) {
            toast.error(msg.message ?? JSON.stringify(msg));
          }
        }
        setCartIsLoading(false);
        let errorMessage = 'Failed to add item to cart';

        if (
          error?.message &&
          typeof error.message === 'string' &&
          error.message !== '[object Object]'
        ) {
          errorMessage = error.message;
        } else if (
          error?.data?.message &&
          typeof error.data.message === 'string'
        ) {
          errorMessage = error.data.message;
        } else if (
          error?.response?.data?.message &&
          typeof error.response.data.message === 'string'
        ) {
          errorMessage = error.response.data.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        return { item, success: false, message: errorMessage };
      }
    },
    [fetchUpdateCart, setCartIsLoading, setCartToast]
  );

  const createQuote = async ({
    item,
    type = 'guest',
    options,
  }: CartPropTypes): Promise<
    { item: CartTypes; success: boolean; message: string } | any
  > => {
    let cartId: string = storage.get(USERS_TYPES.guest.id) ?? '';
    let quoteId: string = storage.get(USERS_TYPES.customer.id) ?? '';
    if (!cartId && !quoteId) {
      const generatedId = await generateCartId();
      if (generatedId?.key === 'cart_id') {
        cartId = generatedId?.value;
        return await onCart({
          item,
          id: { cart_id: generatedId?.value },
          options,
          type,
        });
      } else {
        quoteId = generatedId?.value;
        return await onCart({
          item,
          id: { quote_id: generatedId?.value },
          options,
          type,
        });
      }
    }

    if (cartId) {
      return await onCart({ item, id: { cart_id: cartId }, options, type });
    }
    if (quoteId) {
      return await onCart({ item, id: { quote_id: quoteId }, options, type });
    }
  };

  const updateCart = useCallback(
    async ({ item, id }: CartPropTypes) => {
      const ENDPOINT = id?.quote_id
        ? `${endpoints.CUSTOMER_CART}/items/${item?.item_id}`
        : `${endpoints.GUEST_CART}/${id?.cart_id}/items/${item?.item_id}`;

      const request = {
        cartItem: { ...item },
      };

      if (id?.quote_id) request.cartItem.quote_id = id.quote_id;

      const sku = item?.sku;

      try {
        setCartIsLoading(true);

        if (sku) {
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            const existingItem = newMap.get(sku);
            if (existingItem) {
              newMap.set(sku, { ...existingItem, isLoading: true });
            }
            return newMap;
          });
        }

        const { response, data } = await http.put(ENDPOINT, request);
        if (!response?.ok) {
          throw new Error(data?.message);
        }

        setCartItems(prev =>
          prev?.map(cartItem =>
            cartItem.sku === sku ? { ...cartItem, qty: data?.qty } : cartItem
          )
        );

        if (sku) {
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            const existingItem = newMap.get(sku);
            if (existingItem) {
              newMap.set(sku, {
                ...existingItem,
                qty: data?.qty,
                isLoading: false,
              });
            }
            return newMap;
          });
        }

        return { item, success: true };
      } catch (error: any) {
        Sentry.captureException(error, {
          tags: { module: 'cart', action: 'updateCart' },
          extra: {
            sku: item?.sku,
            qty: item?.qty,
            itemId: item?.item_id,
            isLoggedIn: !!userDetails?.id,
            cartId: id?.cart_id,
            quoteId: id?.quote_id,
            errorMessage: JSON.stringify(error),
          },
        });
        if (sku) {
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            const existingItem = newMap.get(sku);
            if (existingItem) {
              newMap.set(sku, { ...existingItem, isLoading: false });
            }
            return newMap;
          });
        }
        if (error?.message !== '"%fieldName" is required. Enter and try again.')
          toast.error(error?.message);
        return { item, success: false };
      } finally {
        setCartIsLoading(false);
      }
    },
    [setCartIsLoading]
  );

  const generateCartId = async () => {
    const data = await generateCartIdAction();
    if (data) {
      const parsedData = JSON.parse(data);
      if (parsedData?.value) {
        storage.set(parsedData?.key, parsedData?.value);
        return { key: parsedData?.key, value: parsedData?.value };
      }
      toast.error('Error Occurred while generating cart Id');
    }
    toast.error('Error Occurred while generating cart Id');
  };

  const checkCookies = async ({
    cartId,
    quoteId,
  }: {
    cartId?: string;
    quoteId?: string;
  }) => {
    const token = storage.get('token');
    await checkCookiesAction({ cartId, quoteId, token });
  };

  // Update processQueue with proper types
  const processQueue = useCallback(async () => {
    if (isProcessingQueue || cartQueue.current.length === 0) return;

    setIsProcessingQueue(true);
    while (cartQueue.current.length > 0) {
      const nextItem = cartQueue.current[0];
      try {
        const isLoggedIn = !!storage.get('token');
        const { product, method, multiple = false, options } = nextItem;
        const { callback, showToast = true } = options || {};
        let response: CartOperationResult = { item: product, success: false };
        const request: any = {
          sku: product.sku,
          qty: product.qty === 0 ? 0 : product.qty || 1,
          product_option: {
            extension_attributes: {
              custom_options: [],
              configurable_item_options: [],
            },
          },
        };

        if (method === 'put') {
          request.item_id = product.item_id;
        }

        if (product?.gift?.option_id) {
          request.product_option.extension_attributes.custom_options.push({
            option_id: product?.gift?.option_id,
            option_value: product?.gift?.option_value || '0',
          });
        }

        if (product?.giftMessage?.option_id) {
          request.product_option.extension_attributes.custom_options.push({
            option_id: product?.giftMessage?.option_id,
            option_value: product?.giftMessage?.option_value || '',
          });
        }

        if (product?.size?.option_id) {
          request.product_option.extension_attributes.configurable_item_options.push(
            {
              option_id: product?.size?.option_id,
              option_value: product?.size?.option_value,
            }
          );
        }

        if (method === 'put') {
          const id = {
            quote_id: '',
            cart_id: '',
          };

          if (userDetails?.firstname) {
            id.quote_id = product.quote_id ?? '';
          } else {
            id.cart_id = storage.get('cart_id') || '';
          }

          response = await updateCart({
            item: { ...request, name: product.name },
            id,
            multiple,
          });

          if (options?.updateCart) {
            await fetchUpdateCart();
          }
          if (callback) callback();
        } else {
          if (isLoggedIn) {
            response = await createQuote({
              item: { ...request, name: product.name },
              type: 'customer',
              options: { showToast, updateCart: options?.updateCart },
            });
          } else {
            response = await createQuote({
              item: { ...request, name: product.name },
              options: { showToast, updateCart: options?.updateCart },
            });
          }
          if (callback) callback();
        }
        nextItem.resolve(response);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: 'cart', action: 'processQueue' },
          extra: {
            sku: nextItem.product?.sku,
            method: nextItem.method,
            isLoggedIn: !!userDetails?.id,
          },
        });
        nextItem.resolve({ item: nextItem.product, success: false });
        logger.error('Error in processQueue', error);
      }

      cartQueue.current.shift();
    }

    setIsProcessingQueue(false);
  }, [
    isProcessingQueue,
    updateCart,
    fetchUpdateCart,
    createQuote,
    userDetails,
  ]);

  const addToCart = useCallback(
    async (
      product: CartTypes,
      method = 'post',
      multiple = false,
      options?: {
        callback?: () => void;
        showToast?: boolean;
      }
    ) => {
      const isLoggedIn = storage.get('token');
      const { callback, showToast = true } = options || {};
      let response = { item: product, success: false };
      const request: any = {
        sku: product.sku?.toString(),
        qty: product.qty === 0 ? 0 : product.qty || 1,
        product_option: {
          extension_attributes: {
            custom_options: [],
            configurable_item_options: [],
            ...(product?.bundle_options
              ? { bundle_options: product.bundle_options }
              : {}),
          },
        },
      };

      if (method === 'put') {
        request.item_id = product.item_id;
      }
      if (product?.gift?.option_id) {
        // gift
        request.product_option.extension_attributes.custom_options.push({
          option_id: product?.gift?.option_id,
          option_value: product?.gift?.option_value || '0',
        });
      }
      if (product?.giftMessage?.option_id) {
        // gift message
        request.product_option.extension_attributes.custom_options.push({
          option_id: product?.giftMessage?.option_id,
          option_value: product?.giftMessage?.option_value || '',
        });
      }
      if (product?.greeting_card?.option_id) {
        // greeting card
        request.product_option.extension_attributes.custom_options.push({
          option_id: product?.greeting_card?.option_id,
          option_value: product?.greeting_card?.option_value || '',
        });
      }
      if (product?.recipient_name?.option_id) {
        // greeting card
        request.product_option.extension_attributes.custom_options.push({
          option_id: product?.recipient_name?.option_id,
          option_value: product?.recipient_name?.option_value || '',
        });
      }
      if (product?.selectedGiftBoxItems) {
        // selected gift box items
        request.product_option.extension_attributes.gift_box_items =
          product?.selectedGiftBoxItems;
      }
      if (product?.size?.option_id) {
        // package size
        request.product_option.extension_attributes.configurable_item_options.push(
          {
            option_id: product?.size?.option_id,
            option_value: product?.size?.option_value,
          }
        );
      }
      if (method === 'put') {
        const id = {
          quote_id: '',
          cart_id: '',
        };

        // Prefer token-based check for logged-in state
        if (isLoggedIn) {
          // Ensure quote_id is present; refetch from storage if missing
          id.quote_id = product.quote_id || storage.get('quote_id') || '';
        } else {
          // Logged out: ensure guest cart id exists (re-generate if needed)
          id.cart_id = storage.get('cart_id') || '';
          if (!id.cart_id) {
            const generated = await generateCartId();
            if (generated?.key === 'cart_id') {
              storage.set('cart_id', generated.value);
              id.cart_id = generated.value;
            }
          }
        }

        response = await updateCart({
          item: { ...request, name: product.name },
          id,
          multiple,
        });

        // If user is logged out and update failed, retry via POST-like flow
        if (!isLoggedIn && !response?.success) {
          const postRequest: any = { ...request };
          delete postRequest.item_id;
          response = await createQuote({
            item: { ...postRequest, name: product.name },
            options: { showToast },
          });
        }

        await fetchUpdateCart();
        if (callback) callback();
        return response;
      }
      if (isLoggedIn) {
        response = await createQuote({
          item: { ...request, name: product.name },
          type: 'customer',
          options: { showToast },
        });
        return response;
      } else {
        response = await createQuote({
          item: { ...request, name: product.name },
          options: { showToast },
        });
        if (callback) callback();
        return response;
      }
    },
    [userDetails, updateCart, fetchUpdateCart]
  );

  // Update addToCart with proper return type
  const addToCartProduct = useCallback(
    async (
      product: CartTypes,
      method: 'put' | 'post' = 'post',
      multiple = false,
      options?: {
        callback?: () => void;
        showToast?: boolean;
        updateCart?: boolean;
      }
    ): Promise<CartOperationResult> => {
      return new Promise(resolve => {
        // Reset quantity to 1 for new items
        if (method === 'post') {
          product.qty = 1;
        }

        const resolvedOptions = {
          callback: options?.callback,
          showToast: options?.showToast ?? true,
          updateCart: options?.updateCart ?? true,
        };

        cartQueue.current.push({
          product,
          method,
          multiple,
          options: resolvedOptions,
          resolve,
        });

        processQueue();
      });
    },
    [processQueue]
  );

  const removeFromCart = useCallback(
    async (item: any) => {
      const cartId = storage.get(USERS_TYPES.guest.id);
      const isLoggedIn = storage.get('token');
      const itemSku = item?.sku;
      const itemId = item?.item_id;

      const ENDPOINT = isLoggedIn
        ? `${endpoints.CUSTOMER_CART}/items/${itemId}`
        : `${endpoints.GUEST_CART}/${cartId}/items/${itemId}`;

      try {
        setCartIsLoading(true);
        // Step 1: Remove linked greeting card items if it's a gift hamper
        if (itemSku) {
          const existingItem = cartItemsMap.get(itemSku);
          if (existingItem) {
            const isGiftHamper =
              existingItem?.extension_attributes?.is_gift_hamper === '1';
            if (isGiftHamper) {
              const greetingCardOption =
                existingItem?.product_option?.extension_attributes?.custom_options.find(
                  (option: any) =>
                    option?.option_value?.includes('greeting_card')
                );

              if (greetingCardOption) {
                try {
                  const linkedSkus = JSON.parse(
                    greetingCardOption.option_value
                  )?.greeting_card;
                  if (linkedSkus) {
                    const skuList = linkedSkus.split(',');

                    for (const linkedSku of skuList) {
                      const linkedItem = cartItemsMap.get(linkedSku);
                      if (linkedItem) {
                        const linkedItemId = linkedItem?.item_id;

                        const linkedEndpoint = isLoggedIn
                          ? `${endpoints.CUSTOMER_CART}/items/${linkedItemId}`
                          : `${endpoints.GUEST_CART}/${cartId}/items/${linkedItemId}`;

                        await http.delete(linkedEndpoint, {
                          cartItem: {
                            sku: linkedSku,
                            qty: 1,
                            ...(isLoggedIn && { quote_id: item?.quote_id }),
                          },
                        });

                        setCartItemsMap(prev => {
                          const newMap = new Map(prev);
                          newMap.delete(linkedSku);
                          return newMap;
                        });
                        setCartItems(prev =>
                          prev?.filter(ci => ci.sku !== linkedSku)
                        );
                      }
                    }
                  }
                } catch (err) {
                  logger.error(
                    '[removeFromCart] Failed parsing linked SKUs:',
                    err
                  );
                }
              }
            }
          }
          // Set isLoading flag
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(itemSku);
            if (existing) {
              newMap.set(itemSku, { ...existing, isLoading: true });
            }
            return newMap;
          });
        }

        // Step 2: Delete main item
        const res = await http.delete(ENDPOINT, {
          cartItem: {
            sku: itemSku,
            qty: item?.qty,
            ...(isLoggedIn && { quote_id: item?.quote_id }),
          },
        });

        if (res?.data) {
          setCartItems(prev => prev?.filter(ci => ci.sku !== itemSku));
          setCartItemsMap(prev => {
            const newMap = new Map(prev);
            newMap.delete(itemSku);
            return newMap;
          });

          // GA4 Cart: remove_from_cart
          pushGA4Event('remove_from_cart', {
            item_id: itemSku,
          });

          setCartToast({
            message: `"${item?.name}" removed from cart`,
            type: 'success',
          });
          setTimeout(() => setCartToast(undefined), 2000);

          pushEvent({
            event: 'Removed Product From Cart',
            data: {
              Timestamp: new Date(),
              'Product Name': item?.name,
              'Product ID': itemSku,
              'Quantity Removed': item?.qty,
              'Cart Total Items': cartItems?.length,
              'Cart Amount': cartValue,
            },
          });

          await fetchUpdateCart();
        } else {
          logger.error('[removeFromCart] DELETE response has no data');
          toast.error(GENERIC_ERROR_MESSAGE);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: 'cart', action: 'removeFromCart' },
          extra: {
            sku: itemSku,
            itemId: itemId,
            isLoggedIn: !!userDetails?.id,
            cartId: cartId,
          },
        });
        toast.error(GENERIC_ERROR_MESSAGE);
        logger.error('Error removing item from cart:', error);
      } finally {
        setCartIsLoading(false);
      }
    },
    [
      userDetails,
      cartItems?.length,
      cartValue,
      cartItemsMap,
      fetchUpdateCart,
      setCartIsLoading,
      setCartToast,
      pushEvent,
    ]
  );

  const clearCart = async () => {
    try {
      setCartIsLoading(true);
      // Clear cart states
      setCartItems([]);
      setCartItemsMap(new Map());
      setPaymentDetails(null);
      setMovMessage(undefined);
      setMovAmount(undefined);
      setCartValue(0);

      // Clear cart storage
      storage.remove('cart_id');
      storage.remove('quote_id');

      // Update Clevertap
      updateUserCartClevertap({ cartItems: [] });

      // Fetch updated cart state
      await fetchUpdateCart();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'cart', action: 'clearCart' },
        extra: {
          isLoggedIn: !!userDetails?.id,
          cartItemsCount: cartItems?.length,
        },
      });
      logger.error('Error clearing cart:', error);
    } finally {
      setCartIsLoading(false);
    }
  };

  useEffect(() => {
    const cartId: string = storage.get(USERS_TYPES.guest.id) ?? '';
    const quoteId: string = storage.get(USERS_TYPES.customer.id) ?? '';
    if (cartId || quoteId) {
      if (cartId) {
        checkCookies({ cartId });
      } else {
        checkCookies({ quoteId });
      }
      fetchUpdateCart();
    } else {
      generateCartId();
    }
  }, []);

  useEffect(() => {
    fetchUpdateCart();
  }, [storage.get('store_code')]);

  useEffect(() => {
    // Only call when sidebar is opened and cart has items
    if (isCartSidebarOpen && cartItems.length > 0) {
      getRecommendedProducts();
    }
  }, [isCartSidebarOpen]);

  useEffect(() => {
    if (isCartSidebarOpen) {
      pushEvent({
        event: 'Cart Opened',
        data: {
          'Page URL': window.location.href,
          'Page Name': document.title,
          cart_value: cartValue,
          items_count: cartItemsCount ?? cartItems?.length ?? 0,
        },
      });

      // GA4 Cart: view_cart
      pushGA4Event('view_cart', {
        cart_items: cartItemsCount ?? cartItems?.length ?? 0,
      });
    }
  }, [isCartSidebarOpen]);

  useEffect(() => {
    const updateMovFromCookies = async () => {
      const movCookie = await getCookie('mov');
      if (movCookie) {
        const parsedMov = JSON.parse(movCookie);
        const currentMovAmount = Number(
          parsedMov?.amount?.[storage.get('deliveryType') || 'pickup']
        );
        setMovMessage(
          parsedMov?.message || `Minimum Order Value is ₹${parsedMov}`
        );
        setMovAmount(currentMovAmount);
        if (paymentDetails) {
          const orderValueSubtotal = paymentDetails?.others.find(
            (item: { code: string; value: number }) => item?.code === 'subtotal'
          )?.value;
          if (orderValueSubtotal) {
            setCartValue(orderValueSubtotal);
            if (currentMovAmount <= orderValueSubtotal) {
              toggleMov(false);
            } else {
              toggleMov(true);
            }
          }
        }
      }
    };
    updateMovFromCookies();
    window.addEventListener('storage', updateMovFromCookies);
    return () => {
      window.removeEventListener('storage', updateMovFromCookies);
    };
  }, [paymentDetails]);

  // Add this useEffect to handle cartOpen URL parameter
  useEffect(() => {
    const handleCartOpen = () => {
      const params = new URLSearchParams(window.location.search);
      const cartOpen = params.get('cartOpen');
      if (cartOpen === 'true') {
        // Remove only the cartOpen parameter from the URL without triggering a navigation
        params.delete('cartOpen');
        const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        // Open the cart after a short delay
        setTimeout(() => {
          toggleCartSidebar(true);
        }, 100);
      }
    };

    handleCartOpen();
  }, [toggleCartSidebar]);

  const value: useCartPropTypes = {
    addToCart: addToCart as (
      product: CartTypes,
      method?: 'post' | 'put',
      multiple?: boolean,
      options?: { callback?: () => void; showToast?: boolean }
    ) => Promise<{ item: CartTypes; success: boolean; message: string }>,
    addToCartProduct,
    fetchUpdateCart,
    postLoginCartOperations,
    updateCart,
    cartItems,
    paymentDetails,
    removeFromCart,
    isCartSidebarOpen,
    toggleCartSidebar,
    recommendations,
    loadingRecommended,
    cartToast,
    movDisable,
    movMessage,
    movAmount,
    cartValue,
    cartIsLoading,
    clearCart,
    cartItemsMap,
    isProductPage,
    setIsProductPage,
    cartItemsCount,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useCart = (): useCartPropTypes => useContext(Context);

export default CartProvider;
